import { useEffect, useState } from 'react';
import type { Crcy } from '../types/Crcy';
import { getCrcies } from '../api/crcy';

// Module-level cache shared by every hook caller for the lifetime of the page.
// `inflight` deduplicates concurrent fetches; cleared on failure so the next
// mount retries. Fetched LAZILY — only when this hook first runs (i.e. when
// AssetsPage mounts); never part of any login/startup preload. Cleared on
// logout via clearCrciesCache() — next mount re-fetches.
let cache: Crcy[] | null = null;
let inflight: Promise<Crcy[]> | null = null;

function loadCrcies(): Promise<Crcy[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getCrcies()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearCrciesCache(): void {
  cache = null;
  inflight = null;
}

interface UseCrciesResult {
  data: Crcy[] | null;
  loading: boolean;
  error: string | null;
}

export function useCrcies(): UseCrciesResult {
  const [state, setState] = useState<UseCrciesResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      // Cache may have populated between render and effect (e.g. a sibling
      // instance finished fetching). Adopt it; bail if state already matches.
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadCrcies()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
